'use client';

import type React from 'react';
import { useEffect, useState } from 'react';
import { getOrgBrand } from '../lib/org-brand';

interface AvatarSource {
  readonly url: string;
  readonly circular: boolean;
}

export function OrgAvatar({
  orgName,
  size = 'md',
  imageUrl = null,
}: {
  readonly orgName: string;
  readonly size?: 'sm' | 'md';
  readonly imageUrl?: string | null;
}): React.JSX.Element {
  const brand = getOrgBrand(orgName);

  // Logo preference: the static brand registry first, then a remote logo (e.g.
  // the Clerk org's uploaded GitHub avatar), then the gradient-initial fallback.
  const sources: readonly AvatarSource[] = [
    ...(brand === null
      ? []
      : [{ url: brand.logoUrl, circular: brand.circular === true }]),
    ...(typeof imageUrl === 'string' && imageUrl.length > 0
      ? [{ url: imageUrl, circular: true }]
      : []),
  ];

  const [sourceIndex, setSourceIndex] = useState(0);
  useEffect(() => {
    setSourceIndex(0);
  }, [orgName, imageUrl]);

  const dimensions = size === 'sm' ? 'w-8 h-8' : 'w-10 h-10';
  const fontSize = size === 'sm' ? 'text-sm' : 'text-base';

  const current = sources.at(sourceIndex);
  if (current !== undefined) {
    const shape = current.circular
      ? 'rounded-full object-cover'
      : 'rounded object-contain';
    return (
      <img
        src={current.url}
        alt={`${orgName} logo`}
        className={`${dimensions} ${shape}`}
        onError={() => {
          setSourceIndex((index) => index + 1);
        }}
      />
    );
  }

  return (
    <span
      className={`${dimensions} bg-gradient-to-br from-blue-500 to-blue-600 rounded text-white flex items-center justify-center ${fontSize} font-bold flex-shrink-0`}
    >
      {orgName.charAt(0).toUpperCase()}
    </span>
  );
}
